<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import AdminLayout from '@/components/layout/AdminLayout.vue'
import PayoutStatusChip from '@/components/organisations/PayoutStatusChip.vue'
import ConfirmDialog from '@/components/shared/ConfirmDialog.vue'
import OrganisationFeeOverrideSlideOver from '@/components/organisations/OrganisationFeeOverrideSlideOver.vue'
import OrganisationStorageCard from '@/components/organisations/OrganisationStorageCard.vue'
import OrganisationStorageQuotaSlideOver from '@/components/organisations/OrganisationStorageQuotaSlideOver.vue'
import AdjustCreditsSlideOver from '@/components/organisations/AdjustCreditsSlideOver.vue'
import InvoiceChannelSlideOver from '@/components/organisations/InvoiceChannelSlideOver.vue'
import { useOrganisations } from '@/composables/useOrganisations'
import { useOrganisationFeeOverride } from '@/composables/useOrganisationFeeOverride'
import { useOrganisationStorageQuota } from '@/composables/useOrganisationStorageQuota'
import { useOrganisationCredits } from '@/composables/useOrganisationCredits'
import { formatBasisPoints, formatCents } from '@/lib/pricingFormat'
import { formatRelative } from '@/lib/scheduledTaskFormat'
import { InvoiceDeliveryChannel } from '@/types/invoicing'
import type { OrganisationInvoiceChannel, OrganisationInvoicingSummary } from '@/types/invoicing'
import type { CreditLedgerType, OrganisationCredits } from '@/types/credits'
import type { OrganisationFeeOverride, PlatformFeeSource } from '@/types/pricing'
import type { AdminStorageQuota } from '@/types/organisations'

const { t } = useI18n()
const route = useRoute()
const router = useRouter()
const { detail, loading, error, fetchDetail } = useOrganisations()
const {
  override,
  loading: feeLoading,
  error: feeError,
  actionLoading: clearActionLoading,
  actionError: clearActionError,
  fetchOverride,
  clearOverride,
} = useOrganisationFeeOverride()

const {
  quota: storageQuota,
  loading: storageLoading,
  error: storageError,
  actionLoading: storageClearLoading,
  actionError: storageClearError,
  fetchQuota,
  clearOverride: clearStorageOverride,
} = useOrganisationStorageQuota()

const {
  credits,
  loading: creditsLoading,
  error: creditsError,
  fetchCredits,
} = useOrganisationCredits()

const orgId = Number(route.params.id)

const overridePanelOpen = ref(false)
const clearConfirmOpen = ref(false)
const clearReason = ref('')

function sourceBadgeLabel(source: PlatformFeeSource): string {
  return source === 'organisation_override' ? t('admin.organisations.fee_negotiated') : t('admin.organisations.fee_default')
}

function sourceBadgeClass(source: PlatformFeeSource): string {
  return source === 'organisation_override' ? 'bg-amber-500/20 text-amber-400' : 'bg-slate-700 text-slate-400'
}

const hasAnyOverride = computed(() => override.value?.override_percentage_basis_points !== null || override.value?.override_fixed_cents !== null)

function openOverridePanel(): void {
  overridePanelOpen.value = true
}

function handleOverrideUpdated(updated: OrganisationFeeOverride): void {
  override.value = updated
}

function openClearConfirm(): void {
  clearReason.value = ''
  clearConfirmOpen.value = true
}

function handleClearDialogUpdate(open: boolean): void {
  if (clearActionLoading.value) return
  clearConfirmOpen.value = open
}

async function handleConfirmClear(): Promise<void> {
  if (clearReason.value.trim().length < 10) return
  // clearOverride() writes the resolved (now-default) fee straight into
  // `override` — the DELETE endpoint returns the same shape as the GET
  // used for the initial load, so no second round trip is needed.
  const success = await clearOverride(orgId, clearReason.value.trim())
  if (success) {
    clearConfirmOpen.value = false
  }
}

const storagePanelOpen = ref(false)
const storageClearOpen = ref(false)
const storageClearReason = ref('')

function handleStorageUpdated(updated: AdminStorageQuota): void {
  storageQuota.value = updated
}

function openStorageClearConfirm(): void {
  storageClearReason.value = ''
  storageClearError.value = null
  storageClearOpen.value = true
}

function handleStorageClearDialogUpdate(open: boolean): void {
  if (storageClearLoading.value) return
  storageClearOpen.value = open
}

async function handleConfirmStorageClear(): Promise<void> {
  if (storageClearReason.value.trim().length < 10) return
  // The DELETE endpoint returns the resolved (now platform-default) state.
  const success = await clearStorageOverride(orgId, storageClearReason.value.trim())
  if (success) {
    storageClearOpen.value = false
  }
}

const creditsPanelOpen = ref(false)

function creditTypeLabel(type: CreditLedgerType): string {
  const map: Record<CreditLedgerType, string> = {
    earned: t('admin.organisations.credits_type_earned'),
    purchased: t('admin.organisations.credits_type_purchased'),
    consumed: t('admin.organisations.credits_type_consumed'),
    refunded: t('admin.organisations.credits_type_refunded'),
    adjusted: t('admin.organisations.credits_type_adjusted'),
  }
  return map[type] ?? type
}

function handleCreditsAdjusted(updated: OrganisationCredits): void {
  credits.value = updated
}

const invoicingPanelOpen = ref(false)
const invoicingSummary = ref<OrganisationInvoicingSummary | null>(null)

// Seeded from the detail payload; kept locally so a saved override shows without a refetch.
watch(
  detail,
  (value) => {
    invoicingSummary.value = value?.invoicing ?? null
  },
  { immediate: true },
)

function invoiceChannelLabel(channel: InvoiceDeliveryChannel): string {
  return channel === InvoiceDeliveryChannel.Peppol
    ? t('admin.invoicing.channel_peppol')
    : t('admin.invoicing.channel_email')
}

function handleInvoiceChannelUpdated(updated: OrganisationInvoiceChannel): void {
  if (invoicingSummary.value === null) return
  invoicingSummary.value = {
    ...invoicingSummary.value,
    delivery_channel_override: updated.delivery_channel_override,
    derived_channel: updated.derived_channel,
  }
}

onMounted(async () => {
  await Promise.all([fetchDetail(orgId), fetchOverride(orgId), fetchQuota(orgId), fetchCredits(orgId)])
})

const payoutsEnabledLabel = computed<string>(() => {
  const enabled = detail.value?.payout_account.payouts_enabled
  if (enabled === null || enabled === undefined) return t('admin.organisations.payouts.unknown')
  return enabled ? t('admin.organisations.payouts.yes') : t('admin.organisations.payouts.no')
})

const payoutDueBy = computed<string>(() => {
  const due = detail.value?.payout_account.requirements_due_at
  return due ? new Date(due).toLocaleDateString() : '-'
})

const payoutLastSynced = computed<string>(() => {
  const synced = detail.value?.payout_account.status_synced_at
  return synced ? formatRelative(synced) : t('admin.organisations.payouts.never')
})
</script>

<template>
  <AdminLayout>
    <div class="space-y-6">
      <!-- Back link -->
      <button
        type="button"
        class="text-slate-400 hover:text-white text-sm flex items-center gap-1 transition-colors"
        @click="router.push({ name: 'admin.organisations.index' })"
      >
        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" />
        </svg>
        {{ t('admin.organisations.back') }}
      </button>

      <!-- Loading state -->
      <div v-if="loading" class="space-y-4">
        <div class="h-8 bg-slate-800 rounded w-1/3 animate-pulse" />
        <div class="h-4 bg-slate-800 rounded w-1/4 animate-pulse" />
      </div>

      <!-- Error / not found state -->
      <div
        v-else-if="error"
        class="bg-red-500/10 border border-red-500/30 rounded-2xl p-6"
      >
        <p class="text-red-300 text-sm">
          {{ error === 'admin.common.not_found' ? t('admin.organisations.not_found') : t('admin.common.error_loading') }}
        </p>
      </div>

      <!-- Organisation detail -->
      <div v-else-if="detail" class="space-y-6">
        <!-- Header -->
        <div class="bg-slate-800 border border-slate-700 rounded-2xl p-6">
          <div class="flex items-start justify-between gap-4 flex-wrap">
            <h1 class="text-2xl font-bold text-white">{{ detail.name }}</h1>
            <RouterLink
              :to="{ name: 'admin.finance.transactions.index', query: { organisation_id: String(orgId) } }"
              class="border border-slate-600 hover:border-slate-500 text-slate-300 hover:text-white font-semibold rounded-lg px-4 py-2 text-sm transition-colors shrink-0"
            >
              {{ t('admin.organisations.view_transactions') }}
            </RouterLink>
          </div>
          <div class="mt-3 flex flex-wrap gap-4 text-sm text-slate-400">
            <span>{{ t('admin.organisations.slug') }}: <span class="text-slate-300">{{ detail.slug }}</span></span>
            <span>{{ t('admin.organisations.email') }}: <span class="text-slate-300">{{ detail.email }}</span></span>
            <span>{{ t('admin.organisations.city') }}: <span class="text-slate-300">{{ detail.city }}, {{ detail.country_code }}</span></span>
            <span>
              {{ t('admin.organisations.payouts.column') }}:
              <PayoutStatusChip :status="detail.payout_account.status" class="ml-1" />
            </span>
          </div>
        </div>

        <!-- Payout account (provider-neutral status, issue #237) -->
        <div class="bg-slate-800 border border-slate-700 rounded-2xl p-6" data-testid="payout-account-block">
          <h2 class="text-lg font-semibold text-white mb-3">{{ t('admin.organisations.payouts.title') }}</h2>
          <dl class="grid grid-cols-1 gap-3 text-sm sm:grid-cols-2 lg:grid-cols-5">
            <div>
              <dt class="text-xs text-slate-500">{{ t('admin.organisations.payouts.status') }}</dt>
              <dd class="mt-1"><PayoutStatusChip :status="detail.payout_account.status" /></dd>
            </div>
            <div>
              <dt class="text-xs text-slate-500">{{ t('admin.organisations.payouts.accepts_payments') }}</dt>
              <dd class="mt-1 text-slate-300" data-testid="payout-accepts-payments">
                {{ detail.payout_account.accepts_payments ? t('admin.organisations.payouts.yes') : t('admin.organisations.payouts.no') }}
              </dd>
            </div>
            <div>
              <dt class="text-xs text-slate-500">{{ t('admin.organisations.payouts.payouts_enabled') }}</dt>
              <dd class="mt-1 text-slate-300" data-testid="payout-payouts-enabled">
                {{ payoutsEnabledLabel }}
              </dd>
            </div>
            <div>
              <dt class="text-xs text-slate-500">{{ t('admin.organisations.payouts.details_due_by') }}</dt>
              <dd class="mt-1 text-slate-300" data-testid="payout-due-by">{{ payoutDueBy }}</dd>
            </div>
            <div>
              <dt class="text-xs text-slate-500">{{ t('admin.organisations.payouts.last_synced') }}</dt>
              <dd class="mt-1 text-slate-300" data-testid="payout-last-synced">{{ payoutLastSynced }}</dd>
            </div>
          </dl>
        </div>

        <!-- Processing fee override — the only place a negotiated fee is edited or displayed -->
        <div class="bg-slate-800 border border-slate-700 rounded-2xl p-6">
          <div class="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <h2 class="text-lg font-semibold text-white mb-3">{{ t('admin.organisations.fee_title') }}</h2>

              <!-- Loading state -->
              <div v-if="feeLoading" class="space-y-2">
                <div class="h-4 bg-slate-700 rounded w-48 animate-pulse" />
                <div class="h-4 bg-slate-700 rounded w-40 animate-pulse" />
              </div>

              <!-- Error state -->
              <p v-else-if="feeError" class="text-sm text-red-300">{{ t(feeError) }}</p>

              <!-- Data state -->
              <div v-else-if="override" class="space-y-2">
                <div class="flex items-center gap-2">
                  <span class="text-sm text-slate-400 w-40 shrink-0">{{ t('admin.pricing.field_fee_percentage') }}</span>
                  <span class="text-sm text-white">{{ formatBasisPoints(override.effective_percentage_basis_points) }}</span>
                  <span
                    class="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium"
                    :class="sourceBadgeClass(override.percentage_source)"
                  >
                    {{ sourceBadgeLabel(override.percentage_source) }}
                  </span>
                </div>
                <div class="flex items-center gap-2">
                  <span class="text-sm text-slate-400 w-40 shrink-0">{{ t('admin.pricing.field_fee_fixed') }}</span>
                  <span class="text-sm text-white">{{ formatCents(override.effective_fixed_cents) }}</span>
                  <span
                    class="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium"
                    :class="sourceBadgeClass(override.fixed_source)"
                  >
                    {{ sourceBadgeLabel(override.fixed_source) }}
                  </span>
                </div>
              </div>
            </div>
            <div v-if="override" class="flex items-center gap-3 shrink-0">
              <button
                v-if="hasAnyOverride"
                type="button"
                class="border border-slate-600 hover:border-slate-500 text-slate-300 hover:text-white font-semibold rounded-lg px-4 py-2 text-sm transition-colors"
                @click="openClearConfirm"
              >
                {{ t('admin.organisations.fee_clear') }}
              </button>
              <button
                type="button"
                class="bg-emerald-500 hover:bg-emerald-400 text-white font-semibold rounded-lg px-4 py-2 text-sm transition-colors"
                @click="openOverridePanel"
              >
                {{ t('admin.organisations.fee_edit') }}
              </button>
            </div>
          </div>
        </div>

        <!-- Media storage quota (issue #258) -->
        <OrganisationStorageCard
          :quota="storageQuota"
          :loading="storageLoading"
          :error="storageError"
          @edit="storagePanelOpen = true"
          @clear="openStorageClearConfirm"
        />

        <!-- Invoicing: delivery channel override -->
        <div v-if="invoicingSummary" class="bg-slate-800 border border-slate-700 rounded-2xl p-6" data-testid="invoicing-card">
          <div class="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <h2 class="text-lg font-semibold text-white mb-3">{{ t('admin.invoicing.org_title') }}</h2>
              <div class="space-y-2">
                <div class="flex items-center gap-2">
                  <span class="text-sm text-slate-400 w-40 shrink-0">{{ t('admin.invoicing.org_current') }}</span>
                  <span class="text-sm text-white" data-testid="invoicing-current-channel">
                    {{
                      invoicingSummary.delivery_channel_override
                        ? invoiceChannelLabel(invoicingSummary.delivery_channel_override)
                        : t('admin.invoicing.org_automatic', { channel: invoiceChannelLabel(invoicingSummary.derived_channel) })
                    }}
                  </span>
                </div>
                <div class="flex items-center gap-2">
                  <span class="text-sm text-slate-400 w-40 shrink-0">{{ t('admin.invoicing.org_peppol_identifier') }}</span>
                  <span class="text-sm text-white font-mono">
                    {{ invoicingSummary.peppol_identifier ?? t('admin.invoicing.org_none') }}
                  </span>
                </div>
              </div>
            </div>
            <button
              type="button"
              class="bg-emerald-500 hover:bg-emerald-400 text-white font-semibold rounded-lg px-4 py-2 text-sm transition-colors shrink-0"
              data-testid="invoicing-edit"
              @click="invoicingPanelOpen = true"
            >
              {{ t('admin.invoicing.org_edit') }}
            </button>
          </div>
        </div>

        <!-- Credits -->
        <div class="bg-slate-800 border border-slate-700 rounded-2xl overflow-hidden">
          <div class="px-6 py-4 flex items-start justify-between gap-4 flex-wrap">
            <div>
              <h2 class="text-lg font-semibold text-white">{{ t('admin.organisations.credits_title') }}</h2>
              <div v-if="creditsLoading" class="mt-2 h-8 bg-slate-700 rounded w-32 animate-pulse" />
              <p v-else-if="creditsError" class="mt-2 text-sm text-red-300">{{ t(creditsError) }}</p>
              <p v-else-if="credits" class="mt-1 text-2xl font-bold text-white">
                {{ t('admin.organisations.credits_balance', { count: credits.balance.toLocaleString() }) }}
              </p>
            </div>
            <button
              v-if="credits"
              type="button"
              class="bg-emerald-500 hover:bg-emerald-400 text-white font-semibold rounded-lg px-4 py-2 text-sm transition-colors shrink-0"
              @click="creditsPanelOpen = true"
            >
              {{ t('admin.organisations.credits_adjust') }}
            </button>
          </div>
          <template v-if="credits">
            <div v-if="credits.entries.length === 0" class="px-6 py-8 text-center text-slate-400 text-sm border-t border-slate-700">
              {{ t('admin.organisations.credits_empty') }}
            </div>
            <table v-else class="w-full text-sm border-t border-slate-700">
              <thead>
                <tr class="border-b border-slate-700">
                  <th class="text-left px-4 py-3 text-slate-400 font-medium">{{ t('admin.organisations.credits_column_type') }}</th>
                  <th class="text-left px-4 py-3 text-slate-400 font-medium">{{ t('admin.organisations.credits_column_description') }}</th>
                  <th class="text-right px-4 py-3 text-slate-400 font-medium">{{ t('admin.organisations.credits_column_amount') }}</th>
                  <th class="text-right px-4 py-3 text-slate-400 font-medium">{{ t('admin.organisations.credits_column_date') }}</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="entry in credits.entries"
                  :key="entry.id"
                  class="border-b border-slate-700 last:border-0"
                >
                  <td class="px-4 py-3">
                    <span
                      class="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium"
                      :class="entry.amount >= 0 ? 'bg-emerald-500/20 text-emerald-400' : 'bg-red-500/20 text-red-400'"
                    >
                      {{ creditTypeLabel(entry.type) }}
                    </span>
                  </td>
                  <td class="px-4 py-3 text-slate-300">{{ entry.description ?? '-' }}</td>
                  <td
                    class="px-4 py-3 text-right font-mono font-medium"
                    :class="entry.amount >= 0 ? 'text-emerald-400' : 'text-red-400'"
                  >
                    {{ entry.amount >= 0 ? '+' : '' }}{{ entry.amount.toLocaleString() }}
                  </td>
                  <td class="px-4 py-3 text-right text-slate-400 text-xs whitespace-nowrap">
                    {{ new Date(entry.created_at).toLocaleDateString() }}
                  </td>
                </tr>
              </tbody>
            </table>
            <p
              v-if="credits.entries.length === 20"
              class="px-6 py-3 text-xs text-slate-500 border-t border-slate-700"
            >
              {{ t('admin.organisations.credits_recent_caption') }}
            </p>
          </template>
        </div>

        <!-- Events section -->
        <div class="bg-slate-800 border border-slate-700 rounded-2xl overflow-hidden">
          <div class="px-6 py-4 border-b border-slate-700">
            <h2 class="text-lg font-semibold text-white">{{ t('admin.organisations.events_section') }}</h2>
          </div>
          <div v-if="detail.events.length === 0" class="px-6 py-8 text-center text-slate-400 text-sm">
            {{ t('admin.organisations.no_events') }}
          </div>
          <table v-else class="w-full text-sm">
            <thead>
              <tr class="border-b border-slate-700">
                <th class="text-left px-4 py-3 text-slate-400 font-medium">{{ t('admin.organisations.event_name') }}</th>
                <th class="text-left px-4 py-3 text-slate-400 font-medium">{{ t('admin.organisations.slug') }}</th>
                <th class="text-left px-4 py-3 text-slate-400 font-medium">{{ t('admin.organisations.event_website') }}</th>
                <th class="text-left px-4 py-3 text-slate-400 font-medium">{{ t('admin.organisations.created_at') }}</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="event in detail.events"
                :key="event.id"
                class="border-b border-slate-700 last:border-0"
              >
                <td class="px-4 py-3 text-white">{{ event.name }}</td>
                <td class="px-4 py-3 text-slate-400 font-mono text-xs">{{ event.slug }}</td>
                <td class="px-4 py-3">
                  <span
                    class="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium"
                    :class="event.website_enabled
                      ? 'bg-emerald-500/20 text-emerald-400'
                      : 'bg-slate-700 text-slate-400'"
                  >
                    {{ event.website_enabled
                      ? t('admin.organisations.event_website_enabled')
                      : t('admin.organisations.event_website_disabled') }}
                  </span>
                </td>
                <td class="px-4 py-3 text-slate-400 text-xs">
                  {{ new Date(event.created_at).toLocaleDateString() }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Members section -->
        <div class="bg-slate-800 border border-slate-700 rounded-2xl overflow-hidden">
          <div class="px-6 py-4 border-b border-slate-700">
            <h2 class="text-lg font-semibold text-white">{{ t('admin.organisations.members_section') }}</h2>
          </div>
          <div v-if="detail.members.length === 0" class="px-6 py-8 text-center text-slate-400 text-sm">
            {{ t('admin.organisations.no_members') }}
          </div>
          <table v-else class="w-full text-sm">
            <thead>
              <tr class="border-b border-slate-700">
                <th class="text-left px-4 py-3 text-slate-400 font-medium">{{ t('admin.organisations.member_name') }}</th>
                <th class="text-left px-4 py-3 text-slate-400 font-medium">{{ t('admin.organisations.member_email') }}</th>
                <th class="text-left px-4 py-3 text-slate-400 font-medium">{{ t('admin.organisations.member_role') }}</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="member in detail.members"
                :key="member.id"
                class="border-b border-slate-700 last:border-0"
              >
                <td class="px-4 py-3 text-white">{{ member.name }}</td>
                <td class="px-4 py-3 text-slate-300">{{ member.email }}</td>
                <td class="px-4 py-3 text-slate-400 capitalize">{{ member.role ?? '—' }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <!-- Edit override slide-over -->
    <OrganisationFeeOverrideSlideOver
      :open="overridePanelOpen"
      :organisation-id="orgId"
      :current-percentage-basis-points="override?.override_percentage_basis_points ?? null"
      :current-fixed-cents="override?.override_fixed_cents ?? null"
      @close="overridePanelOpen = false"
      @updated="handleOverrideUpdated"
    />

    <!-- Storage quota override slide-over -->
    <OrganisationStorageQuotaSlideOver
      v-if="storageQuota"
      :open="storagePanelOpen"
      :organisation-id="orgId"
      :current-override-bytes="storageQuota.override_quota_bytes"
      :effective-quota-bytes="storageQuota.effective_quota_bytes"
      :used-bytes="storageQuota.used_bytes"
      @close="storagePanelOpen = false"
      @updated="handleStorageUpdated"
    />

    <!-- Adjust credits slide-over -->
    <AdjustCreditsSlideOver
      :open="creditsPanelOpen"
      :organisation-id="orgId"
      :current-balance="credits?.balance ?? 0"
      @close="creditsPanelOpen = false"
      @adjusted="handleCreditsAdjusted"
    />

    <!-- Invoice delivery channel slide-over -->
    <InvoiceChannelSlideOver
      v-if="invoicingSummary"
      :open="invoicingPanelOpen"
      :organisation-id="orgId"
      :current="invoicingSummary"
      @close="invoicingPanelOpen = false"
      @updated="handleInvoiceChannelUpdated"
    />

    <!-- Clear override confirmation -->
    <ConfirmDialog
      :open="clearConfirmOpen"
      :title="t('admin.organisations.fee_clear')"
      :body="t('admin.organisations.fee_clear_confirm')"
      :confirm-label="t('admin.organisations.fee_clear')"
      :loading="clearActionLoading"
      :danger="true"
      :disable-confirm="clearReason.trim().length < 10"
      @update:open="handleClearDialogUpdate"
      @confirm="handleConfirmClear"
    >
      <div>
        <label for="clear-override-reason" class="block text-xs font-medium text-slate-300 mb-1">
          {{ t('admin.pricing.reason_label') }}
        </label>
        <textarea
          id="clear-override-reason"
          v-model="clearReason"
          rows="2"
          maxlength="500"
          class="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-1.5 text-sm text-white focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 outline-none"
        />
        <p v-if="clearActionError" class="mt-1 text-xs text-red-400">{{ t(clearActionError) }}</p>
      </div>
    </ConfirmDialog>

    <!-- Clear storage quota override confirmation -->
    <ConfirmDialog
      :open="storageClearOpen"
      :title="t('admin.organisations.storage_clear')"
      :body="t('admin.organisations.storage_clear_confirm')"
      :confirm-label="t('admin.organisations.storage_clear')"
      :loading="storageClearLoading"
      :danger="true"
      :disable-confirm="storageClearReason.trim().length < 10"
      @update:open="handleStorageClearDialogUpdate"
      @confirm="handleConfirmStorageClear"
    >
      <div>
        <label for="clear-storage-reason" class="block text-xs font-medium text-slate-300 mb-1">
          {{ t('admin.pricing.reason_label') }}
        </label>
        <textarea
          id="clear-storage-reason"
          v-model="storageClearReason"
          rows="2"
          maxlength="500"
          class="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-1.5 text-sm text-white focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 outline-none"
        />
        <p v-if="storageClearError" class="mt-1 text-xs text-red-400">{{ t(storageClearError) }}</p>
      </div>
    </ConfirmDialog>
  </AdminLayout>
</template>
